import grapesjs, { Editor } from 'grapesjs';
import GjsEditor from '@grapesjs/react';
import basicPlugin from 'grapesjs-blocks-basic';
import 'grapesjs/dist/css/grapes.min.css';
import { useEffect, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import type { introduceEditorUpdateMutation as UpdateMutationType } from './__generated__/introduceEditorUpdateMutation.graphql'
import type { introduceEditorQuery as IntroduceQueryType } from './__generated__/introduceEditorQuery.graphql'
import type { introduceEditorTemplateSelectorQuery as TemplateType } from './__generated__/introduceEditorTemplateSelectorQuery.graphql'
import { useUserStore } from '@/defines/store/userStore';
import AlertService from '@/defines/service/alertService';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import TemplateSelector from '../admin/templateSelector';
import CommonService from '@/defines/service/commonService';
import { Switch } from '@headlessui/react';

export const IntroduceEditorQuery = graphql`
    query introduceEditorQuery(
        $userId: String
    ) {
        insurance_agent_connection(where: {id: {_eq: $userId}}) {
        edges {
          cursor
          node {
            contenthtml
            contentjson
            userpubliccontent
            id
          }
        }
      }
    }
`

const IntroduceEditorUpdatePublicMutation = graphql`
    mutation introduceEditorUpdatePublicMutation(
        $userId: String!
        $public: Boolean!
    ) {
        update_insurance_agent_by_pk(
            pk_columns: {id: $userId},
            _set: {
                userpubliccontent: $public
            }
        ) {
            id
        }
    }
`;

const IntroduceEditorUpdateMutation = graphql`
    mutation introduceEditorUpdateMutation(
        $userId: String!
        $contenthtml: String
        $contentjson: String
    ) {
        update_insurance_agent_by_pk(
            pk_columns: {id: $userId},
            _set: {
                contenthtml: $contenthtml,
                contentjson: $contentjson,
            }
        ) {
            id
        }
    }
`;

const IntroduceEditorTemplateSelectorQuery = graphql`
  query introduceEditorTemplateSelectorQuery
  {
    grapes_template_connection {
        edges {
            node {
                id
                contenthtml
                description
                imageurl
                name
            }
        }
    }
  }
`;

interface InstroduceInterface {
  contenthtml: string | undefined;
  contentjson: string | undefined;
  userpubliccontent: boolean;
  id: string;
}

export default function IntroduceEditor() {
  const s3BaseUrl = import.meta.env.VITE_AWS_S3_BASE_URL;
  const [reloadEditor, setReloadEditor] = useState(true); // Because the grapesJS editor is not re-rendering after the first time, we need to force it to re-render by changing this state
  const [data, setData] = useState({} as InstroduceInterface);
  const [commitUpdate] = useMutation<UpdateMutationType>(IntroduceEditorUpdateMutation);
  const [commitUpdatePublic] = useMutation(IntroduceEditorUpdatePublicMutation);
  const alertService = AlertService();
  let editor = {} as Editor;

  const queryData = useLazyLoadQuery<IntroduceQueryType>(
    IntroduceEditorQuery,
    { userId: useUserStore().userData.id! },
  );

  const templateData = useLazyLoadQuery<TemplateType>(
    IntroduceEditorTemplateSelectorQuery,
    {},
  );

  useEffect(() => {
    if (queryData.insurance_agent_connection.edges.length === 0) {
      return;
    }
    var data = queryData.insurance_agent_connection.edges[0].node;
    console.log('Anesth Data:', data);
    var forAddAnesthData = {} as InstroduceInterface;

    forAddAnesthData.contenthtml = data.contenthtml ?? '';
    forAddAnesthData.contentjson = data.contentjson ?? '';
    forAddAnesthData.userpubliccontent = data.userpubliccontent;
    forAddAnesthData.id = CommonService().DecodeNodeId(data.id);
    setData(forAddAnesthData);

  }, [queryData]);

  const onEditor = (_editor: Editor) => {
    editor = _editor;
    console.log('Editor loaded', { editor: _editor });

    _editor.Panels.addButton('options', {
      id: 'save',
      className: 'fa fa-floppy-o',
      attributes: {
        title: 'Save data'
      },
      command(editor: any) {
        let components = editor.getComponents();
        let style = editor.getStyle()
        let templateData = {
          components: components,
          style: style
        };
        console.log(JSON.stringify(templateData.components));
        editor.store();
        alertService.Success('儲存成功 ! ');
      }
    });

    _editor.Panels.addButton('options', {
      id: 'load-templates',
      className: 'fa fa-folder-open',
      attributes: {
        title: 'load-templates'
      },
      command(editor: any) {
        const modal = editor.Modal;

        const title = document.createElement('div');
        title.innerText = '請選擇樣板';
        modal.setTitle(title);

        const el = document.createElement('div');
        const hospitalSelectorContainer = document.createElement('div');
        const root = createRoot(hospitalSelectorContainer);
        root.render(<TemplateSelector templateData={templateData} onSelect={(content: string) => {
          let templateData = JSON.parse(content);
          editor.addComponents(templateData);
          // editor.addStyle(templateData.style);
        }}></TemplateSelector>);
        el.appendChild(hospitalSelectorContainer);
        modal.setContent(el);
        modal.open();
      }
    });
  };


  const toBase64 = (file: File) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    return '';
  });

  const AssetUpload = async (file: File) => {
    var base64 = String(await toBase64(file));
    const resizedImg = await getResizeImg(base64, 300);

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${useUserStore.getState().token}`
    }

    const payload = {
      fileName: 'InsuranceIntroImages/' + crypto.randomUUID() + '.png',
      fileData: resizedImg,
      bucketName: 'gd-static-files'
    };

    // Send POST request to your Lambda endpoint
    const response = await axios.post(import.meta.env.VITE_SERVERLESS_API + '/file/upload', payload, {
      headers: headers
    });

    return s3BaseUrl + '/' + response.data.fileName;
  }

  const hasuraStoragePlugin = (editor: Editor) => {
    // As sessionStorage is not an asynchronous API,
    // the `async` keyword could be skipped
    editor.Storage.add('hasura', {
      async load() {
        return JSON.parse(data.contentjson!);
      },

      async store(dataFromEditor) {
        // async store(data) {
        let json = JSON.stringify(dataFromEditor);
        console.log('data:', data);
        console.log('json:', json);

        setData({ ...data, contentjson: json, contenthtml: editor.getHtml() });

        commitUpdate({
          variables: {
            userId: data.id,
            contenthtml: editor.getHtml(),
            contentjson: json,
          },
          onCompleted: () => {
            console.log('Insurance Updated');
            editor.refresh();
          },
          onError: (error) => {
            alertService.Error('更新失敗 ! ');
            console.error('Error Updating Insurance', error);
          }
        });

        return Promise.resolve();
      }
    });
  };

  const getResizeImg = async (imageSrc: string, fileLimit: number) => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) return '';

    let width = image.width;
    let height = image.height;
    const aspectRatio = fileLimit / width;
    canvas.width = fileLimit;
    canvas.height = height * aspectRatio;

    ctx!.drawImage(
      image, 0, 0,
      image.width,
      image.height,
      0,
      0,
      canvas.width,
      canvas.height
    )

    return new Promise<string>((resolve) => {
      resolve(canvas.toDataURL('image/jpeg'));
    })
  }
  const createImage = (url: string) =>
    new Promise<HTMLImageElement>((resolve) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.setAttribute('crossOrigin', 'anonymous')
      image.src = url;
      return image;
    });
  const submit = async () => {
    commitUpdate({
      variables: {
        userId: data.id,
        contenthtml: data.contenthtml,
        contentjson: data.contentjson,
      },
      onCompleted: () => {
        console.log('Anesth Updated');
        alertService.Success('您的資訊已更新 ! ');
        setReloadEditor(false);
      },
      onError: (error) => {
        alertService.Error('更新失敗 ! ');
        console.error('Error Updating Anesth', error);
      }
    });
  }
  const publicSet = () => {
    commitUpdatePublic({
      variables: {
        userId: data.id,
        public: !data.userpubliccontent
      },
      onCompleted: () => {
        setData({ ...data, userpubliccontent: !data.userpubliccontent });
        alertService.Info('更新成功 ! ');
        console.log('Insurance Updated');
      },
      onError: (error) => {
        alertService.Error('更新失敗 ! ');
        console.error('Error Updating Insurance', error);
      }
    });
  }

  useEffect(() => {
    setReloadEditor(true);
  }, [reloadEditor]);

  if (!useUserStore().insuranceData.allowUseIntroduce) {
    return (
      <h1 className='p-2 text-3xl mx-auto my-auto'>請先升級為群體版本後才可享有自介編輯功能</h1>
    )
  }

  return (

    <>
      <div className="mx-auto w-full mt-12 pt-6 " style={{ maxWidth: "1300px" }}>
        <div className='flex items-baseline justify-between border-b border-gray-200'>
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">
            自介編輯
          </h1>
          <div>

            <div className="flex m-2">
              <h3 className="mx-2 text-base font-semibold text-gray-900">設定是否顯示</h3>
              <Switch
                checked={data.userpubliccontent}
                onChange={() => {
                  publicSet();
                }}
                className="ml-2 group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                    bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-400 
                    focus:ring-offset-2 data-[checked]:bg-red-400"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition 
                      duration-200 ease-in-out group-data-[checked]:translate-x-5"
                />
              </Switch>
            </div>
          </div>
        </div>
        <p className="text-sm">以下資料將會修改後自動儲存 ! </p>
      </div>




      <div className="mt-4 grid sm:grid-cols-6 grid-cols-1 gap-x-6 gap-y-8 mx-4" >
        {
          reloadEditor ?
            <div className='col-span-6 flex justify-center items-center bg-gray-200'>
              <GjsEditor
                className='my-2 mx-2'
                grapesjs={grapesjs}
                grapesjsCss="@/lib/grapesJS/grapes.min.css"
                options={{
                  deviceManager: {
                    devices: [
                      {
                        name: '電腦',
                        width: '1000px',
                      },
                      {
                        name: '手機',
                        width: '375px',
                      },
                    ]
                  },
                  height: '100vh',
                  storageManager: {
                    autosave: true,
                    stepsBeforeSave: 1,
                    type: 'hasura'
                  },
                  assetManager: {
                    uploadFile: async (e: any) => {
                      const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
                      var url = await AssetUpload(files[0]);
                      editor?.AssetManager.add(url);
                    }
                  },

                  style: `
                      img {
                          object-fit: cover;
                      }
                          `,

                  components: ``,


                  plugins:
                    [
                      basicPlugin, hasuraStoragePlugin
                    ],

                }}
                plugins={[
                  { id: 'grapesjs-tailwind', src: 'https://unpkg.com/grapesjs-tailwind', options: {} },
                ]}

                onEditor={onEditor}
              />
            </div>
            :
            <></>
        }

        <div className="sm:col-span-6 pb-12 flex flex-row-reverse">
          <button
            type="button"
            onClick={submit}
            className="rounded-md bg-red-400 px-3.5 py-2.5 text-base font-semibold text-white shadow-sm hover:bg-red-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-400"
          >
            手動儲存
          </button>
        </div>

      </div>


    </>
  );

}